import React from 'react';
import styled from 'styled-components';

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(10, minmax(50px, 1fr));
  gap: 0.45rem;
  padding: 0.4rem 0.45rem 0.75rem;
  margin-bottom: 0.25rem;
  border-radius: 8px;
  background: #ffffff;
  overflow-x: hidden;
  overflow-y: hidden;

  @media (max-width: 600px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    row-gap: 0.8rem;
    column-gap: 0.2rem;
    padding: 0.3rem 0.15rem 0.75rem;
  }
`;

const Digit = styled.div<{ $accent: string; $active: boolean }>`
  position: relative;
  width: 50px;
  height: 50px;
  margin: auto;
  border-radius: 50%;
  border: 2px solid #a9bfd2;
  border-bottom-color: ${props => props.$accent};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #172033;
  background: rgba(255, 255, 255, 0.35);
  box-shadow: 0 4px 10px rgba(55, 87, 120, 0.2);

  @media (max-width: 600px) {
    width: clamp(36px, 13vw, 46px);
    height: clamp(36px, 13vw, 46px);
  }

  &::after {
    content: ${props => props.$active ? "'▲'" : "''"};
    position: absolute;
    bottom: -13px;
    color: #3b82f6;
    font-size: 0.72rem;
  }
`;

const DigitNumber = styled.strong`
  font-size: 0.88rem;
  line-height: 1;
`;

const Percentage = styled.span`
  margin-top: 0.15rem;
  font-size: 0.58rem;
  line-height: 1;
  font-weight: 700;
`;

interface DigitFrequencyProps {
  percentages: number[];
  currentDigit: number;
}

const DigitFrequency: React.FC<DigitFrequencyProps> = ({ percentages, currentDigit }) => {
  const values = percentages.length === 10 ? percentages : Array(10).fill(0);
  const ranked = values
    .map((percentage, digit) => ({ percentage, digit }))
    .sort((a, b) => a.percentage - b.percentage || a.digit - b.digit);
  const lowest = ranked[0].digit;
  const secondLowest = ranked[1].digit;
  const highest = ranked[ranked.length - 1].digit;
  const secondHighest = ranked[ranked.length - 2].digit;

  const accentFor = (digit: number) => {
    if (digit === highest) return '#22c55e';
    if (digit === lowest) return '#ef4444';
    if (digit === secondLowest) return '#f59e0b';
    if (digit === secondHighest) return '#3b82f6';
    return '#a9bfd2';
  };

  return (
    <Strip aria-label="Live digit frequency">
      {values.map((percentage, digit) => (
        <Digit key={digit} $accent={accentFor(digit)} $active={digit === currentDigit}>
          <DigitNumber>{digit}</DigitNumber>
          <Percentage>{percentage.toFixed(2)}%</Percentage>
        </Digit>
      ))}
    </Strip>
  );
};

export default DigitFrequency;
